import React, { createContext, useContext } from 'react';
import useDropDownItemsHook, {
  DropDownItemsHook,
} from './hooks/useDropDownItemsHook';
import useFiltersHook, { FiltersHook } from './hooks/useFiltersHooks';
import useLabelsHook, { LabelsHook } from './hooks/useLabelsHooks';
import useLanguageHook, { LanguageHook } from './hooks/useLanguageHooks';
import useProjectDataHook, {
  ProjectDataHook,
} from './hooks/useProjectDataHook';
import useProjectsOverviewHook, {
  ProjectsOverviewHook,
} from './hooks/useProjectsOverviewHook';
import useShowFilterHook, { ShowFilterHook } from './hooks/useShowFilterHook';
import { useWindowWidthHook } from './hooks/useWindowSizeHook';
import useSitemapHook, { SitemapHook } from './hooks/useSitemapHook';
import { initials, Store } from './store';
import { DropDownItemsStoreType } from './store/dropDown/dropDownItemsStore.types';
import { FiltersStoreType } from './store/filters/filtersStore.types';
import { LabelsStoreType } from './store/labels/labelsStore.types';
import { LanguageStoreType } from './store/language/languageStore.types';
import { ProjectDataStoreType } from './store/projectData/projectDataStore.types';
import { ProjectsOverviewStoreType } from './store/projectsOverview/projectsOverviewStore.types';
import { ShowFilterStoreType } from './store/showFilter/showFilterStore.types';
import { WindowWidthStoreType } from './store/windowWidth/windowWidthStore.types';
import useBaseUrlHook, { BaseUrlHook } from './hooks/useBaseUrlHook';
import { BaseUrlStoreType } from './store/baseUrl/baseUrlStore.types';
import { SitemapStoreType } from './store/sitemap/sitemapStore.types';
import { ProjectRequestCountStoreType } from './store/projectRequestCount/projectRequestCountStore.types';

type ActionsType = ProjectsOverviewHook['actions'] &
  DropDownItemsHook['actions'] &
  FiltersHook['actions'] &
  LabelsHook['actions'] &
  BaseUrlHook['actions'] &
  SitemapHook['actions'] &
  LanguageHook['actions'] &
  ShowFilterHook['actions'] &
  ProjectDataHook['actions'];

const Actions = createContext<ActionsType>({} as ActionsType);
const ProjectsOverview = createContext<Store['projectsOverview']>(
  initials.projectsOverview
);
const DropDownItems = createContext<Store['dropDown']>(initials.dropDown);
const FilterSettings = createContext<Store['filters']>(initials.filters);
const LabelsContext = createContext<Store['labels']>(initials.labels);
const SitemapContext = createContext<Store['sitemap']>(initials.sitemap);
const BaseUrlContext = createContext<Store['baseUrl']>(initials.baseUrl);
const LanguageContext = createContext<Store['language']>(initials.language);
const ShowFilterContext = createContext<Store['showFilter']>(
  initials.showFilter
);
const ProjectDataContext = createContext<Store['projectData']>(
  initials.projectData
);
const ProjectRequestCountStoreContext = createContext<
  Store['projectRequestCount']
>(initials.projectRequestCount);
const WindowWidthContext = createContext<Store['isMobileView']>(
  initials.isMobileView
);

export const StoreProvider = ({
  children,
}: {
  children: JSX.Element;
}): JSX.Element => {
  const {
    state: projectsOverviewStore,
    actions: { getProjects },
    prcState: projectRequestCountStore,
  } = useProjectsOverviewHook();
  const {
    state: dropDownItemsStore,
    actions: { getDropDownItemsAction },
  } = useDropDownItemsHook();
  const {
    state: filtersStore,
    actions: {
      setFilters,
      setDropDownItem,
      resetFilters,
      setFiltersRatingMinRatingMax,
      setFiltersSearchTerm,
      setPage,
      setSearchTerm,
      setSize,
      setSorting,
    },
  } = useFiltersHook();

  const {
    state: baseUrlStore,
    actions: {
      setBaseUrlLanguage,
      initBaseUrl,
      getLanguageByPath,
      getReportStartYearAndEndYear,
    },
  } = useBaseUrlHook();

  const {
    state: languageStore,
    actions: { setLanguage, setFirstLanguageChangeDone },
  } = useLanguageHook();

  const {
    state: labelStore,
    actions: { initializeLabels },
  } = useLabelsHook();

  const {
    state: sitemapStore,
    actions: { getSitemap },
  } = useSitemapHook();

  const {
    state: showFilterStore,
    actions: { setShowFilter },
  } = useShowFilterHook();

  const {
    state: projectDataStore,
    actions: { getProject },
  } = useProjectDataHook();

  const { state: windowWidthStore } = useWindowWidthHook();

  const actions = {
    getProjects,
    getDropDownItemsAction,
    setDropDownItem,
    resetFilters,
    setFilters,
    setFiltersSearchTerm,
    setFiltersRatingMinRatingMax,
    initializeLabels,
    setBaseUrlLanguage,
    initBaseUrl,
    getLanguageByPath,
    getReportStartYearAndEndYear,
    getSitemap,
    setLanguage,
    setFirstLanguageChangeDone,
    setPage,
    setSize,
    setSearchTerm,
    setSorting,
    setShowFilter,
    getProject,
  };
  return (
    <ProjectsOverview.Provider value={projectsOverviewStore}>
      <WindowWidthContext.Provider value={windowWidthStore}>
        <DropDownItems.Provider value={dropDownItemsStore}>
          <FilterSettings.Provider value={filtersStore}>
            <ProjectDataContext.Provider value={projectDataStore}>
              <BaseUrlContext.Provider value={baseUrlStore}>
                <LabelsContext.Provider value={labelStore}>
                  <ProjectRequestCountStoreContext.Provider
                    value={projectRequestCountStore}
                  >
                    <SitemapContext.Provider value={sitemapStore}>
                      <ShowFilterContext.Provider value={showFilterStore}>
                        <LanguageContext.Provider value={languageStore}>
                          <Actions.Provider value={actions}>
                            {children}
                          </Actions.Provider>
                        </LanguageContext.Provider>
                      </ShowFilterContext.Provider>
                    </SitemapContext.Provider>
                  </ProjectRequestCountStoreContext.Provider>
                </LabelsContext.Provider>
              </BaseUrlContext.Provider>
            </ProjectDataContext.Provider>
          </FilterSettings.Provider>
        </DropDownItems.Provider>
      </WindowWidthContext.Provider>
    </ProjectsOverview.Provider>
  );
};
export const useLanguageStore = (): LanguageStoreType =>
  useContext(LanguageContext);
export const useLabels = (): LabelsStoreType => useContext(LabelsContext);
export const useFilterSettingsStore = (): FiltersStoreType =>
  useContext(FilterSettings);
export const useSitemapStore = (): SitemapStoreType =>
  useContext(SitemapContext);
export const useDropDownItemsStore = (): DropDownItemsStoreType =>
  useContext(DropDownItems);
export const useProjectsOverviewStore = (): ProjectsOverviewStoreType =>
  useContext(ProjectsOverview);
export const useShowFilterStore = (): ShowFilterStoreType =>
  useContext(ShowFilterContext);
export const useProjectDataStore = (): ProjectDataStoreType =>
  useContext(ProjectDataContext);
export const useWindowWidthStore = (): WindowWidthStoreType =>
  useContext(WindowWidthContext);
export const useBaseUrlStore = (): BaseUrlStoreType =>
  useContext(BaseUrlContext);
export const useProjectRequestCountStore = (): ProjectRequestCountStoreType =>
  useContext(ProjectRequestCountStoreContext);
export const useAction = (): ActionsType => useContext(Actions);
